import { Granularity, TimeRangeConfig } from '@/api/generated/model';
import { ChartMetric } from '@/types/userIntell/chartView';

const EXPLICIT_GRANULARITIES = new Set<string>(Object.values(Granularity));

/** Effective granularity for charts/filters when config omits an explicit value. */
export function resolveEffectiveGranularity(
  granularity: string | null | undefined,
  timeRange?: TimeRangeConfig,
): Granularity {
  if (granularity && EXPLICIT_GRANULARITIES.has(granularity)) {
    return granularity as Granularity;
  }

  if (!timeRange) {
    return 'day';
  }

  const fallbackGranularity = getProperGranularity(timeRange);

  if (fallbackGranularity === 'week' || fallbackGranularity === 'month') {
    return fallbackGranularity;
  }

  return 'day';
}

/**
 * Get proper granularity based on the time range,
 *
 * since might be overwhelming to get:
 * @example
 * - hourly data, for a 1 year range
 * - daily data, for a 12 months range
 * - ... and so on
 *
 * @param timeRange
 * @param chartMetric
 * @returns proper granularity
 */
export function getProperGranularity(timeRange: TimeRangeConfig, chartMetric?: ChartMetric): Granularity {
  const trk = timeRange.kind;

  if (chartMetric === 'retention') {
    switch (trk) {
      case 'today':
      case 'yesterday':
      case '7_days':
      case '30_days':
      case 'this_month':
      case 'this_quarter':
      case '6_months':
        return 'week';
      case '12_months':
        return 'month';
      case 'custom':
        return getGranularityFromCustomTimeRange(timeRange);
      default:
        return 'month';
    }
  }

  switch (trk) {
    case 'today':
    case 'yesterday':
    case '7_days':
    case 'this_month':
      return 'day';
    case '30_days':
    case '3_months':
    case 'this_quarter':
    case '6_months':
      return 'week';
    case '12_months':
      return 'month';
    case 'custom':
      return getGranularityFromCustomTimeRange(timeRange);
    default:
      return 'month';
  }
}

export function getTimeRangeDiff(timeRange: TimeRangeConfig) {
  return (new Date(timeRange.end ?? Date.now()).getTime() - new Date(timeRange.start).getTime()) / 1000;
}

function getGranularityFromCustomTimeRange(timeRange: TimeRangeConfig) {
  const oneDay = 60 * 60 * 24;
  const sevenDays = oneDay * 7;
  const oneYear = oneDay * 365;

  const diff = getTimeRangeDiff(timeRange);

  if (diff <= sevenDays) return 'day';
  if (diff < oneYear) return 'week';
  return 'month';
}
